'use client';

import { FC, useEffect } from 'react';
import { ReadonlyURLSearchParams, useSearchParams } from 'next/navigation';

import { TEMPLATE_BASE_PATH } from '@/constants';
import {
  stringToSlug,
  toQueryFilters,
  toQueryParams,
  toTreezFilters,
  toTreezSortBy,
} from '@/utils';
import { useBundleSpecial, useSelectedStore } from '@/hooks';
import { useUiStore } from '@/data';
import {
  Breadcrumb,
  CollectionTreez,
  Container,
  FiltersTreez,
  Heading,
  Link,
  ProductsProvider,
  SpecialPlaceholder,
  useProductsContext,
} from '@/components';

import { BREADCRUMB_ITEMS } from './contants';
import { IConfigRefinementBundleSpecials } from './types';

const SpecialComponents = () => {
  const searchParams = useSearchParams();
  const bundleId = searchParams?.get('special_id') ?? '';
  const { store } = useSelectedStore();
  const [, setState] = useUiStore();
  const { isLoading, special } = useBundleSpecial({
    id: bundleId,
  });

  const sort = searchParams?.get('sort') ?? '';
  const initialSortBy = toTreezSortBy(sort);

  const query = searchParams?.get('query') ?? '';

  const queryParams = toQueryParams(searchParams as ReadonlyURLSearchParams);
  const treezFilters = toTreezFilters(queryParams);
  const initialFilters = toQueryFilters(treezFilters);

  return (
    <main id="main" role="main">
      <Container as="section" className="special_page">
        <Breadcrumb items={BREADCRUMB_ITEMS}>{special?.title ?? ''}</Breadcrumb>
      </Container>
      {isLoading && <SpecialPlaceholder />}
      {!isLoading && special && (
        <ProductsProvider
          bundleId={bundleId}
          bundleSlug={stringToSlug(special?.title)}
          bundleTitle={special?.title}
          includePromoItems
          initialData={[]}
          initialFilters={initialFilters}
          initialSearchTerm={query}
          initialSortBy={initialSortBy}
          key={bundleId}
          pageSize={100}
        >
          <ConfigRefinementBundleSpecials bundleId={bundleId} />
          <FiltersTreez />
          <CollectionTreez bundleId={bundleId} onSpecials />
        </ProductsProvider>
      )}
      {!special && !isLoading && (
        <Container as="section" className="special_empty">
          <Heading level={5}>
            {store?.shortName
              ? 'Special not found'
              : // eslint-disable-next-line max-len
                'Select your store to unlocked this bundle'}
          </Heading>
          {store?.shortName && (
            <div className="special_empty__actions">
              Click{' '}
              <Link className="action" href={TEMPLATE_BASE_PATH.SHOP}>
                here to visit the Shop Page.
              </Link>
            </div>
          )}
          {!store?.shortName && (
            <div className="special_empty__actions">
              Click{' '}
              <p
                className="action"
                onClick={() =>
                  setState({
                    is_store_location_open: true,
                  })
                }
                role="button"
              >
                here select your store.
              </p>
            </div>
          )}
        </Container>
      )}
    </main>
  );
};

const ConfigRefinementBundleSpecials: FC<IConfigRefinementBundleSpecials> = ({
  bundleId,
}) => {
  const { refine } = useProductsContext();
  const { initDepProducts, initIndProducts, processingIndependents, special } =
    useBundleSpecial({ id: bundleId });

  useEffect(() => {
    if (!special) {
      return;
    }
    const idsDepProd = {
      productId: initDepProducts?.map(prod => prod.id).join(','),
    };
    const idsIndProd = {
      productId: initIndProducts?.map(prod => prod.id).join(','),
    };

    const [depKey, depValue] = Object.entries(idsDepProd)[0];
    const [indKey, indValue] = Object.entries(idsIndProd)[0];

    if (processingIndependents) {
      refine(depKey, depValue, false);
      refine(indKey, indValue, true);
      return;
    }

    refine(indKey, indValue, false);
    refine(depKey, depValue, true);
  }, [special, processingIndependents]);

  return <></>;
};

export default SpecialComponents;
